/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Sitemap addressing and XML (AGL-2520).
 *
 * A site publishes ONE sitemap index at `/sitemap.xml` and a child sitemap per
 * section — pages, authors, one per content collection, and any a plugin
 * declares for its own pages — each of which pages further at
 * {@link SITEMAP_URLS_PER_FILE}.
 *
 * The split is what makes the sitemap survive a site growing. The protocol
 * caps a single sitemap at 50,000 URLs and 50MB, and a flat file has no
 * answer once a site crosses either: the URLs past the cap are simply not
 * submitted, silently. It also makes the sweep behind each file bounded —
 * a crawler asking for `/sitemaps/content-blog/2.xml` reads one page of one
 * collection's entries instead of the whole site.
 *
 * These are pure functions on purpose: the tenant route handler that answers
 * these URLs and the middleware that routes them to it must agree on the
 * shape exactly, and so must the tests.
 */

import { toEpochMs } from './lockdown'

/** Where a site's sitemap index lives; what `robots.txt` advertises. */
export const SITEMAP_INDEX_PATH = '/sitemap.xml'

/**
 * URLs per child sitemap.
 *
 * An order of magnitude under the protocol's 50,000 because the cap that
 * binds first here is not the protocol's but Firestore's: a page of this file
 * is a page of a query, and 5,000 keeps one crawler fetch to 5,000 document
 * reads and a few hundred KB of XML. Raising it trades read cost for fewer
 * files, and nothing in the format cares which side of that trade we take.
 */
export const SITEMAP_URLS_PER_FILE = 5000

/**
 * A ceiling on how many pages one section may advertise, so a runaway
 * collection cannot produce an index with tens of thousands of entries — the
 * index has its own 50,000 limit, and a sweep that large is a bug long before
 * it is a sitemap. 100 pages is 500,000 URLs in one section.
 */
export const SITEMAP_MAX_PAGES_PER_SECTION = 100

/**
 * The platform's fixed sections. A content collection adds one of its own,
 * and a plugin declares any its pages fill (`plugin-sitemap-sections`).
 */
export const SITEMAP_SECTION_PAGES = 'pages'
/**
 * Author pages (AGL-2518) — `/author/{slug}`, one per author record.
 *
 * A fixed section rather than a per-collection one, because there is one
 * author page per PERSON and it spans every collection they wrote in. A
 * `content-…` section per collection would submit the same author several
 * times over, at addresses that no longer exist.
 */
export const SITEMAP_SECTION_AUTHORS = 'authors'

/**
 * The prefix a content collection's section carries.
 *
 * It exists so a collection slugged like a section a plugin declares cannot
 * claim it, and it cannot itself collide: stripping happens ONCE, so a
 * collection actually slugged `content-products` addresses
 * `content-content-products` and stays distinct from `content-products`,
 * which is the collection slugged `products`.
 */
const CONTENT_SECTION_PREFIX = 'content-'

/** The sitemap section a content collection's URLs live in. */
export function contentSitemapSection(collectionSlug: string): string {
  return `${CONTENT_SECTION_PREFIX}${collectionSlug}`
}

/** The collection a `content-…` section names, or `undefined` for the rest. */
export function contentSitemapSectionSlug(
  section: string,
): string | undefined {
  if (!section.startsWith(CONTENT_SECTION_PREFIX)) return undefined
  const slug = section.slice(CONTENT_SECTION_PREFIX.length)
  return slug || undefined
}

/**
 * A child sitemap's path.
 *
 * The page is ALWAYS its own segment, including page 1. A one-file section is
 * the common case and could have been the bare `/sitemaps/{section}.xml`, but
 * then the page number would have to be encoded into the section name for
 * every other page — and `content-blog-2` is ambiguous between page 2 of the
 * collection `blog` and page 1 of the collection `blog-2`, which is a real
 * slug someone can create. A separate segment cannot be ambiguous, and
 * nothing but the index ever writes these URLs.
 */
export function sitemapSectionPath(section: string, page: number): string {
  return `/sitemaps/${section}/${Math.max(1, Math.floor(page))}.xml`
}

/** Matches {@link sitemapSectionPath}; the one parser, shared with the middleware. */
const SITEMAP_SECTION_PATH = /^\/sitemaps\/([^/]+)\/([0-9]+)\.xml$/

/** The section and page a path addresses, or `undefined` if it addresses none. */
export function parseSitemapSectionPath(
  pathname: string,
): { section: string; page: number } | undefined {
  const match = SITEMAP_SECTION_PATH.exec(pathname)
  if (!match) return undefined
  const page = Number(match[2])
  if (!Number.isFinite(page) || page < 1) return undefined
  return { section: decodeURIComponent(match[1]), page }
}

/**
 * How many files a section of `urlCount` URLs needs. Zero URLs means zero
 * files — a section with nothing in it is left out of the index rather than
 * advertised as an empty document a crawler has to fetch to discover is empty.
 */
export function sitemapPageCount(urlCount: number): number {
  if (!Number.isFinite(urlCount) || urlCount <= 0) return 0
  return Math.min(
    SITEMAP_MAX_PAGES_PER_SECTION,
    Math.ceil(urlCount / SITEMAP_URLS_PER_FILE),
  )
}

/** XML text escaping — a slug or a query string may carry any of these. */
export function escapeSitemapXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * One address in a sitemap document. A bare string is an address with no
 * known modification date; the object form carries a `lastmod` when the
 * builder could derive one.
 */
export type SitemapLocation = string | { loc: string; lastmod?: string }

/**
 * A `<lastmod>` value from whatever timestamp shape a document carries
 * (AGL-2647): a Firestore `Timestamp` (`toMillis()`), the `{seconds}` object
 * a serialized one decays into, epoch milliseconds, or an ISO string.
 *
 * The DAY only, in UTC. `YYYY-MM-DD` is a complete W3C datetime, and a date a
 * crawler can compare is worth more than a time it cannot: a value that moved
 * with every render would be a `lastmod` crawlers learn to discount, and the
 * whole point of the element is to be believed.
 *
 * `undefined` for anything that does not decode, and the caller omits the
 * element. Never "now": an invented date claims a change that did not
 * happen, and a sitemap whose dates are consistently wrong is read as having
 * none at all.
 */
export function sitemapLastmod(value: unknown): string | undefined {
  const ms = toEpochMs(value)
  if (ms === undefined || ms <= 0) return undefined
  return new Date(ms).toISOString().slice(0, 10)
}

/** The `lastmod` a location carries, if any. */
export function sitemapLocationLastmod(
  location: SitemapLocation,
): string | undefined {
  return typeof location === 'string' ? undefined : location.lastmod
}

/**
 * The newest of some `lastmod` values, or `undefined` when none is known.
 *
 * What a listing page and an index entry are dated by: a listing changed when
 * its newest entry did, and a child sitemap changed when its newest URL did.
 * Comparison is on the string, which is sound because every value is the
 * fixed-width `YYYY-MM-DD` {@link sitemapLastmod} produces.
 */
export function newestSitemapLastmod(
  lastmods: Iterable<string | undefined>,
): string | undefined {
  let newest: string | undefined
  for (const lastmod of lastmods) {
    if (lastmod && (!newest || lastmod > newest)) newest = lastmod
  }
  return newest
}

/**
 * De-duplicated (AGL-582): a collection slug can shadow a screen path, and
 * the same URL twice in one sitemap is a submission of a duplicate, not a
 * stronger signal. The FIRST occurrence keeps its date — the routing order
 * that decides which page a shadowed URL serves is the order the builders
 * emit in, so the first source is the one whose date describes the page.
 */
function uniqueSitemapLocations(
  locations: readonly SitemapLocation[],
): Array<{ loc: string; lastmod?: string }> {
  const byLoc = new Map<string, { loc: string; lastmod?: string }>()
  for (const location of locations) {
    const entry = typeof location === 'string' ? { loc: location } : location
    if (!byLoc.has(entry.loc)) {
      byLoc.set(entry.loc, { loc: entry.loc, lastmod: entry.lastmod })
    }
  }
  return [...byLoc.values()]
}

/** `<url>` or `<sitemap>`; the `<lastmod>` child only when a date is known. */
function sitemapLocationXml(
  tag: 'url' | 'sitemap',
  { loc, lastmod }: { loc: string; lastmod?: string },
): string {
  const date = lastmod ? `<lastmod>${escapeSitemapXml(lastmod)}</lastmod>` : ''
  return `  <${tag}><loc>${escapeSitemapXml(loc)}</loc>${date}</${tag}>`
}

/** A `<urlset>` document. */
export function sitemapUrlsetXml(urls: readonly SitemapLocation[]): string {
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n' +
    uniqueSitemapLocations(urls)
      .map((item) => sitemapLocationXml('url', item))
      .join('\n') +
    '\n</urlset>\n'
  )
}

/** A `<sitemapindex>` document — the children of `/sitemap.xml`. */
export function sitemapIndexXml(locs: readonly SitemapLocation[]): string {
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n' +
    uniqueSitemapLocations(locs)
      .map((item) => sitemapLocationXml('sitemap', item))
      .join('\n') +
    '\n</sitemapindex>\n'
  )
}
